export const translations = {
  en: {
    dir: 'ltr',
    nav: { home: 'Home', technology: 'Technology', solutions: 'Solutions', about: 'About', contact: 'Contact' },
    brand: { name1: 'DESERT', name2: 'NIMBUS', tag: 'TECHNOLOGIES', sub: 'ENGINEERING TOMORROW’S CLIMATE' },
    hero: {
      live: 'LIVE · ABU DHABI SANDBOX',
      est: 'EST. 2026',
      manifesto: '(00) · MANIFESTO',
      titleA: 'Engineering',
      titleB: 'tomorrow’s',
      titleC: 'climate.',
      body: 'An autonomous network of AI-driven drone swarms delivering precision atmospheric intelligence, weather modification, and environmental resilience — engineered for the harshest climates on Earth.',
      ctaA: 'EXPLORE TECHNOLOGY',
      ctaB: 'REQUEST BRIEFING',
      unit: 'UNIT / DN-M400 · IP55',
      payload: 'PAYLOAD',
      scroll: 'SCROLL',
      metrics: [
        { value: '1000+', label: 'Autonomous drones planned' },
        { value: '50 m', label: 'Micro-climate grid resolution' },
        { value: '180 s', label: 'Deployment reaction time' },
        { value: '75%', label: 'Cheaper than desalination' },
      ],
      ticker: ['Autonomous', 'Micro-Climate', 'Weather Intelligence', 'UAE 2036', 'Cloud Seeding', 'Fog Mitigation', 'Edge AI', 'AeroSwarm', 'PulseCharge', 'Sovereign Tech'],
    },
    about: {
      kicker: '(01) · ABOUT',
      subkicker: 'Our Vision',
      titleA: 'Make climate a',
      titleAccent: 'solvable',
      titleB: 'engineering problem.',
      body: 'We see a future where water security, atmospheric health and infrastructure resilience are no longer at the mercy of the weather — but shaped by intelligent, autonomous systems working in service of the nations that deploy them. DesertNimbus was founded to prove that this future can be engineered, tested and delivered from the desert outward.',
      pillars: [
        { title: 'Water', body: 'Reliable rainfall through targeted, chemical-free electrostatic seeding — closing the gap between demand and desalination.' },
        { title: 'Air', body: 'Continuous atmospheric intelligence at 50-meter resolution — turning weather from a hazard into a managed input.' },
        { title: 'Infrastructure', body: 'Solar arrays, airports and highways defended from dust, fog and thermal stress by autonomous swarms.' },
      ],
      chapters: [
        { n: '01', title: 'The blind spot below the clouds.', body: 'For decades, weather intervention has lived at 30,000 feet — piloted aircraft dispersing chemicals across vast, imprecise volumes. The lower boundary layer, 0 to 3,000 feet, remained a satellite blind spot. That’s the altitude where climate actually meets human life.' },
        { n: '02', title: 'A swarm, not a squadron.', body: 'We replaced the aircraft with a mesh — thousands of autonomous drones sharing intent through AeroSwarm Mesh OS. GPS-denied, sandstorm-tolerant, decentralised. A single mind acting through many bodies, resilient by design.' },
        { n: '03', title: 'Electric charge, not chemistry.', body: 'PulseCharge Targeting emits precise ions directly into cumulus updrafts. Droplets coalesce. Rain follows. No chemical runoff, no storage overhead — up to 75% cheaper per cubic meter than desalination, and safer for the environment we’re trying to protect.' },
        { n: '04', title: 'Sovereign, safety-first.', body: 'Every mission interfaces with UAE UTM. ADS-B in/out, geofenced airspace, dual IMUs, ballistic parachutes. Built for 55°C, IP55 ingress, silica dust. Engineered where others have never flown.' },
      ],
      chapter: 'CHAPTER',
    },
    tech: {
      kicker: '(02) · TECHNOLOGY',
      titleA: 'Three intelligent systems.',
      titleB: 'One atmospheric mission.',
      stackKicker: 'LATEST & MOST ADVANCED STACK',
      stack: 'DesertNimbus is built on the frontier of what’s operationally possible today. Enterprise-grade airframes, custom edge-AI compute, decentralised mesh radio and proprietary ML models trained on regional atmospheric data — engineered for 55 °C, IP55 and GPS-denied airspace.',
      tags: [
        { title: 'Edge AI', body: 'Onboard inference — no cloud dependency' },
        { title: 'Mesh Radio', body: 'Sandstorm-tolerant, GPS-denied capable' },
        { title: 'Sensor Fusion', body: 'LiDAR + thermal + hyperspectral' },
        { title: 'Hardware', body: 'DJI Enterprise · IP55 · 55 °C rated' },
      ],
      system: 'SYSTEM',
      learn: 'LEARN MORE',
      systems: [
        { n: '01', title: 'NimbusPredict AI', lead: 'Predictive atmospheric modeling.', body: 'Ingests thermal, pressure and humidity feeds from hovering drones, fused with NCM Doppler radar arrays, to map micro-climates at 50-meter grid resolution. Flash fog and micro-burst wind anomalies predicted up to four hours in advance.', specs: [ ['Grid Resolution','50 m'], ['Forecast Horizon','4 hours'], ['Data Sources','Drone telemetry + NCM Doppler'] ] },
        { n: '02', title: 'AeroSwarm Mesh OS', lead: 'Decentralised swarm intelligence.', body: 'An autonomous drone-to-drone communication mesh executing flight patterns without constant ground-station or cellular links. Flawless operation in GPS-denied conditions and sandstorm interference.', specs: [ ['Topology','Decentralised mesh'], ['Resilience','GPS-denied capable'], ['Latency','Sub-frame swarm sync'] ] },
        { n: '03', title: 'PulseCharge Targeting', lead: 'Electrical charge, precision aimed.', body: 'Real-time ML identifies the exact thermal updraft core of a cumulus cloud, guiding drones to fire targeted electrical charges. Maximises precipitation from the marginal, thin clouds common across the UAE.', specs: [ ['Method','Electric charge seeding'], ['Chemistry','Zero runoff'], ['Cost Advantage','75% below desalination'] ] },
      ],
    },
    solutions: {
      kicker: '(03) · SOLUTIONS',
      titleA: 'Building a',
      titleAccent: 'climate-resilient',
      titleB: 'future.',
      learn: 'LEARN',
      items: [
        { n: '01', tag: '75% cheaper / m³', kicker: 'Cloud seeding, re-imagined.', title: 'Rain Enhancement', body: 'Automated, targeted electric-pulse emission payloads trigger rapid droplet coalescence in cumulus formations — without chemical runoff. Scales down operations, eliminates storage overhead.' },
        { n: '02', tag: 'Zero flight diversions', kicker: 'Coastal corridors, always clear.', title: 'Fog Mitigation', body: 'Upon AI detection of a dew-point inversion, swarms distribute microscopically fine, biodegradable moisture-binding compounds — precipitating fog and restoring visibility across airports, ports and highways.' },
        { n: '03', tag: 'Continuous yield', kicker: 'Panels protected. Output preserved.', title: 'Solar Infrastructure', body: 'Swarms track low-altitude particulate movement during sandstorms and use low-pressure electrostatic pulses to clear micro-dust from panels — preventing substantial output capacity loss for mega-scale energy installations.' },
        { n: '04', tag: '180s response', kicker: 'Real-time atmospheric awareness.', title: 'Environmental Intelligence', body: 'Continuous monitoring for sandstorm tracking, air-quality mapping and thermal anomaly detection — feeding decision-ready data streams to civil defense, meteorology and infrastructure operators.' },
      ],
    },
    contact: {
      kicker: '(05) · CONTACT',
      titleA: 'Open a channel',
      titleAccent: 'with the sky.',
      body: 'For partnership, investment, government or press enquiries — send a brief transmission. Handled with sovereign discretion.',
      location: 'LOCATION',
      coords: 'COORDINATES',
      channel: 'CHANNEL',
      locationV: 'Abu Dhabi Sandbox · UAE',
      coordsV: 'N 24°28′ · E 54°22′',
      channelV: 'info@desertnimbus.com',
      name: 'YOUR NAME',
      org: 'ORGANIZATION',
      email: 'EMAIL',
      interest: 'NATURE OF INTEREST',
      message: 'YOUR MESSAGE',
      namePh: 'Full name',
      orgPh: 'Company / Agency',
      emailPh: 'you@domain.com',
      messagePh: 'Nature of engagement, mission scope, or enquiry…',
      interestOptions: ['Partnership', 'Investment', 'Government Briefing', 'Press', 'Careers'],
      disclosure: '• TRANSMISSIONS HANDLED WITH SOVEREIGN DISCRETION',
      send: 'SEND TRANSMISSION',
      sent: 'SENT',
      toastSuccessTitle: 'Transmission received',
      toastSuccessBody: 'Handled with sovereign discretion. We’ll respond shortly.',
      toastMissingTitle: 'Missing fields',
      toastMissingBody: 'Please complete name, email and message.',
      signature: 'Signed — The Office of the CEO, DesertNimbus Technologies · Abu Dhabi',
    },
    footer: {
      body: 'An autonomous network of AI-driven drone swarms delivering precision atmospheric intelligence — engineered for the harshest climates on Earth.',
      explore: 'EXPLORE', missions: 'MISSIONS', station: 'STATION', follow: 'FOLLOW',
      missionsList: ['Rain Enhancement', 'Fog Mitigation', 'Solar Defence', 'Env. Intelligence'],
      copyright: '© 2026 DESERTNIMBUS TECHNOLOGIES · SOVEREIGN OPERATOR',
      privacy: 'PRIVACY', terms: 'TERMS', legal: 'LEGAL',
      signatureLead: 'FROM THE OFFICE OF THE CEO',
      signatureName: 'S. Al Mansoori',
      signatureRole: 'Founder & Chief Executive · DesertNimbus Technologies',
    },
  },
  ar: {
    dir: 'rtl',
    nav: { home: 'الرئيسية', technology: 'التقنية', solutions: 'الحلول', about: 'من نحن', contact: 'اتصل بنا' },
    brand: { name1: 'دزرت', name2: 'نمبس', tag: 'للتقنيات', sub: 'هندسة مناخ الغد' },
    hero: {
      live: 'مباشر · مختبر أبوظبي',
      est: 'تأسست 2026',
      manifesto: '(٠٠) · البيان',
      titleA: 'هندسة',
      titleB: 'مناخ',
      titleC: 'الغد.',
      body: 'شبكة ذاتية من أسراب الطائرات المسيّرة بالذكاء الاصطناعي توفر ذكاءً جوياً دقيقاً، وتعديلاً للطقس، ومرونة بيئية — مصممة لأقسى المناخات على الأرض.',
      ctaA: 'استكشف التقنية',
      ctaB: 'اطلب إيجازاً',
      unit: 'الوحدة / DN-M400 · IP55',
      payload: 'الحمولة',
      scroll: 'مرّر',
      metrics: [
        { value: '+1000', label: 'طائرة ذاتية مخططة' },
        { value: '50 م', label: 'دقة شبكة المناخ المصغّر' },
        { value: '180 ث', label: 'زمن الاستجابة للنشر' },
        { value: '75%', label: 'أرخص من تحلية المياه' },
      ],
      ticker: ['ذاتي', 'مناخ مصغّر', 'ذكاء جوي', 'الإمارات 2036', 'استمطار', 'معالجة الضباب', 'ذكاء طرفي', 'أيرو سوارم', 'بلس شارج', 'تقنية سيادية'],
    },
    about: {
      kicker: '(٠١) · من نحن',
      subkicker: 'رؤيتنا',
      titleA: 'جعل المناخ',
      titleAccent: 'مشكلة هندسية',
      titleB: 'قابلة للحل.',
      body: 'نرى مستقبلاً لا يكون فيه أمن المياه وصحة الأجواء ومرونة البنية التحتية رهينة للطقس، بل تصوغها أنظمة ذكية ذاتية تعمل في خدمة الدول التي تنشرها. تأسست دزرتنمبس لتإثبات أن هذا المستقبل يمكن هندسته واختباره وتقديمه من الصحراء إلى العالم.',
      pillars: [
        { title: 'الماء', body: 'أمطار موثوقة عبر استمطار كهروستاتي دقيق خالٍ من المواد الكيميائية — لسدّ الفجوة بين الطلب وتحلية المياه.' },
        { title: 'الهواء', body: 'ذكاء جوي مستمر بدقة 50 متراً — لتحويل الطقس من خطر إلى مدخلٍ مدار.' },
        { title: 'البنية التحتية', body: 'حماية الألواح الشمسية والمطارات والطرق من الغبار والضباب والإجهاد الحراري بواسطة أسراب ذاتية.' },
      ],
      chapters: [
        { n: '01', title: 'النقطة العمياء تحت الغيوم.', body: 'لعقود، أجري التدخل المناخي على ارتفاع 30،000 قدم، بينما ظلت الطبقة الدنيا من 0 إلى 3000 قدم نقطة عمياء للأقمار الصناعية. وهي الارتفاع الذي يلتقي فيه المناخ فعلياً بالحياة البشرية.' },
        { n: '02', title: 'سرب، لا سرية.', body: 'استبدلنا الطائرات بشبكة — آلاف الطائرات الذاتية تتشارك القرار عبر نظام تشغيل مش أيرو سوارم. تعمل دون GPS، وتقاوم العواصف، ولا مركزية لها. عقل واحد يعمل عبر أجسام كثيرة.' },
        { n: '03', title: 'شحنة كهربائية، لا كيمياء.', body: 'يطلق نظام بلستشارج أيونات دقيقة داخل تيارات الرفع الحراري. تتجمّع القطيرات. ثم المطر. لا مخلفات كيميائية، ولا تخزين — وحتى 75% أرخص لكل متر مكعّب من تحلية المياه.' },
        { n: '04', title: 'سيادي، والأمان أولاً.', body: 'تتوافق كل مهمة مع نظام إدارة الحركة الجوية في الإمارات. ADS-B، ومجال جوي محدد جغرافياً، ومظلات طوارئ. مصممة لدرجة 55°م وحماية IP55.' },
      ],
      chapter: 'فصل',
    },
    tech: {
      kicker: '(٠٢) · التقنية',
      titleA: 'ثلاثة أنظمة ذكية.',
      titleB: 'مهمة جوية واحدة.',
      stackKicker: 'أحدث وأرقى منظومة',
      stack: 'بُنيت دزرتنمبس على حدود ما هو ممكن تشغيلياً اليوم. طائرات بمواصفات مؤسّسية، وحوسبة ذكاء طرفية، وراديو مش، ونماذج تعلّم مدرّبة على بيانات جوية إقليمية — مصممة لدرجة 55°م وIP55 ومجال جوي بلا GPS.',
      tags: [
        { title: 'ذكاء طرفي', body: 'استنتاج داخلي — دون اعتماد على السحابة' },
        { title: 'راديو مش', body: 'مقاوم للعواصف، يعمل دون GPS' },
        { title: 'دمج المستشعرات', body: 'ليدار + حراري + فرط طيفي' },
        { title: 'العتاد', body: 'DJI مؤسّسي · IP55 · 55°م' },
      ],
      system: 'النظام',
      learn: 'اعرف المزيد',
      systems: [
        { n: '01', title: 'نمبس بريدكت AI', lead: 'نمذجة جوية تنبؤية.', body: 'يدمج البيانات الحرارية والضغط والرطوبة من الطائرات مع رادارات دوبلر، لرسم مناخات مصغّرة بدقة 50 متراً. يتنبأ بالضباب المفاجئ والرياح المتدفقة حتى أربع ساعات مقدماً.', specs: [ ['دقة الشبكة','50 م'], ['أفق التنبؤ','4 ساعات'], ['المصادر','طائرات + دوبلر'] ] },
        { n: '02', title: 'أيرو سوارم مش OS', lead: 'ذكاء أسراب لا مركزي.', body: 'شبكة اتصال ذاتية بين الطائرات تنفّذ أنماط الطيران دون الحاجة إلى اتصالات أرضية مستمرة. أداء مثالي في غياب GPS.', specs: [ ['البنية','شبكة لا مركزية'], ['المرونة','تعمل دون GPS'], ['الزمن','مزامنة دون إطار'] ] },
        { n: '03', title: 'بلستشارج للاستهداف', lead: 'شحنة كهربائية بدقة متناهية.', body: 'يحدد التعلم الآلي اللحظي قلب تيار الرفع الحراري، لتوجيه الطائرات لإطلاق شحنات دقيقة. يزيد من الأمطار من الغيوم الرقيقة.', specs: [ ['الأسلوب','استمطار كهربائي'], ['الكيمياء','بلا تسرّب'], ['الميزة','75% أقل من التحلية'] ] },
      ],
    },
    solutions: {
      kicker: '(٠٣) · الحلول',
      titleA: 'بناء',
      titleAccent: 'مستقبل مناخي',
      titleB: 'مرن.',
      learn: 'اقرأ',
      items: [
        { n: '01', tag: '75% أرخص / م³', kicker: 'استمطار معاد ابتكاره.', title: 'تعزيز الأمطار', body: 'حمولات موجّهة تُطلق نبضات كهربائية تحفّز سريعاً تجميع القطيرات داخل التشكيلات الركامية — دون أي مخلفات كيميائية.' },
        { n: '02', tag: 'بلا تحويل رحلات', kicker: 'ممرات ساحلية واضحة دائماً.', title: 'معالجة الضباب', body: 'عند رصد الذكاء الاصطناعي لتقلّب نقطة الندى، توزّع الأسراب مركّبات حيوية دقيقة مرتبطة بالرطوبة — لترسيب الضباب وإعادة الرؤية.' },
        { n: '03', tag: 'إنتاج مستمر', kicker: 'حماية الألواح. حفاظ على الإنتاج.', title: 'البنية الشمسية', body: 'تتتبّع الأسراب حركة الجزيئات خلال العواصف الرملية، وتستخدم نبضات كهروستاتية لإزالة الغبار من الألواح — لمنع خسارة الطاقة الإنتاجية.' },
        { n: '04', tag: 'استجابة 180ث', kicker: 'وعي جوي لحظي.', title: 'الذكاء البيئي', body: 'مراقبة مستمرة للعواصف الرملية وجودة الهواء والشذوذ الحراري — تغذّي تدفقات بيانات للدفاع المدني والأرصاد.' },
      ],
    },
    contact: {
      kicker: '(٠٥) · اتصل بنا',
      titleA: 'افتح قناة',
      titleAccent: 'مع السماء.',
      body: 'للشراكة أو الاستثمار أو الجهات الحكومية أو الإعلام — أرسل رسالة موجزة. تُدار بتقدير سيادي.',
      location: 'الموقع',
      coords: 'الإحداثيات',
      channel: 'القناة',
      locationV: 'مختبر أبوظبي · الإمارات',
      coordsV: 'ش 24°28′ · ش 54°22′',
      channelV: 'info@desertnimbus.com',
      name: 'الاسم',
      org: 'المؤسسة',
      email: 'البريد',
      interest: 'طبيعة الاهتمام',
      message: 'رسالتك',
      namePh: 'الاسم الكامل',
      orgPh: 'الشركة / الجهة',
      emailPh: 'you@domain.com',
      messagePh: 'طبيعة التواصل أو الإستفسار…',
      interestOptions: ['شراكة', 'استثمار', 'إيجاز حكومي', 'إعلام', 'وظائف'],
      disclosure: '• تُدار بتقدير سيادي',
      send: 'أرسل الرسالة',
      sent: 'تم الإرسال',
      toastSuccessTitle: 'تم الاستلام',
      toastSuccessBody: 'تُدار بتقدير سيادي. سنرد قريباً.',
      toastMissingTitle: 'حقول ناقصة',
      toastMissingBody: 'الرجاء إكمال الاسم والبريد والرسالة.',
      signature: 'توقيع — مكتب الرئيس التنفيذي، دزرتنمبس للتقنيات · أبوظبي',
    },
    footer: {
      body: 'شبكة ذاتية من الطائرات المسيّرة بالذكاء الاصطناعي، تقدم ذكاءً جوياً دقيقاً — مصمّمة لأقسى المناخات.',
      explore: 'استكشف', missions: 'المهمات', station: 'المحطة', follow: 'تابعنا',
      missionsList: ['تعزيز الأمطار', 'معالجة الضباب', 'دفاع شمسي', 'ذكاء بيئي'],
      copyright: '© 2026 دزرتنمبس للتقنيات · مشغّل سيادي',
      privacy: 'الخصوصية', terms: 'الشروط', legal: 'قانوني',
      signatureLead: 'من مكتب الرئيس التنفيذي',
      signatureName: 'س. المنصوري',
      signatureRole: 'مؤسّس ورئيس تنفيذي · دزرتنمبس للتقنيات',
    },
  },
};

export const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=85',
  systems: [
    'https://images.unsplash.com/photo-1533628635777-112b2239b1c7?auto=format&fit=crop&w=1600&q=85',
    'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&w=1600&q=85',
    'https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?auto=format&fit=crop&w=1600&q=85',
  ],
  solutions: [
    'https://images.unsplash.com/photo-1519692933481-e162a57d6721?auto=format&fit=crop&w=1600&q=85',
    'https://images.unsplash.com/photo-1487621167305-5d248087c724?auto=format&fit=crop&w=1600&q=85',
    'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=85',
    'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1600&q=85',
  ],
};

export const SOCIAL = [
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/desertnimbus', key: 'linkedin' },
  { name: 'X', href: 'https://x.com/desertnimbus', key: 'x' },
  { name: 'Instagram', href: 'https://instagram.com/desertnimbus', key: 'instagram' },
  { name: 'YouTube', href: 'https://youtube.com/@desertnimbus', key: 'youtube' },
];
